import { useEffect, useState } from "react";
import api, { errorStatus } from "../../api/account";
import { AuthUser } from "../../auth/types";
import { EMAIL_AREAS, EmailAreaId } from "./emailAreas";

type EmailPreferencesCardProps = {
  user: AuthUser;
};

/** Order-insensitive list equality — ES5-safe, no Set/includes. */
function sameSet(a: string[], b: string[]): boolean {
  return (
    a.length === b.length && a.every((item) => b.indexOf(item) !== -1)
  );
}

/**
 * Email preferences — a denylist of campaign-mail areas the user has opted
 * out of. Saving is pessimistic (whole-list PUT on demand) rather than
 * auto-save-per-toggle: the service write is a dual-tenant read-modify-write,
 * so three auto-saved toggles would race, and a whole-list PUT has no partial
 * success to reconcile.
 */
export default function EmailPreferencesCard({ user }: EmailPreferencesCardProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [suppressed, setSuppressed] = useState<string[]>([]);
  const [savedSuppressed, setSavedSuppressed] = useState<string[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const res = await api.account.getEmailPreferences(user);
        if (cancelled) return;

        const list = res && res.suppressed ? res.suppressed : [];
        setSuppressed(list);
        setSavedSuppressed(list);
        setLoadFailed(false);
        setError("");
      } catch (err) {
        if (!cancelled) {
          setLoadFailed(true);
          setError(
            `Failed to load preferences (${
              errorStatus(err) ?? "network error"
            }).`
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, reloadKey]);

  const dirty = !sameSet(suppressed, savedSuppressed);

  const toggleArea = (id: EmailAreaId) => {
    setSaved(false);
    setSuppressed((prev) =>
      prev.indexOf(id) === -1
        ? prev.concat([id])
        : prev.filter((item) => item !== id)
    );
  };

  const handleSave = async () => {
    if (!dirty || saving || loading) return;

    setError("");
    setSaving(true);

    try {
      const res = await api.account.setEmailPreferences(suppressed, user);
      const list = res && res.suppressed ? res.suppressed : suppressed;
      setSuppressed(list);
      setSavedSuppressed(list);
      setSaved(true);
    } catch (err) {
      // Keep the working set so the user can simply retry.
      setError(
        `Failed to save preferences (${
          errorStatus(err) ?? "network error"
        }). Please try again.`
      );
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div className="account-card account-card--wide">
        <h2 className="account-card-title">Email preferences</h2>
        <p className="emailpref-loading">Loading your preferences…</p>
      </div>
    );
  }

  // A failed load shows the error and a retry only — rendering the toggles
  // with defaulted state would misrepresent what is actually stored.
  if (loadFailed) {
    return (
      <div className="account-card account-card--wide">
        <h2 className="account-card-title">Email preferences</h2>
        <div className="signin-error" style={{ marginBottom: 14 }}>
          {error}
        </div>
        <button
          className="signin-btn emailpref-save-btn"
          onClick={() => setReloadKey((key) => key + 1)}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="account-card account-card--wide">
      <h2 className="account-card-title">Email preferences</h2>
      <p className="account-card-description">
        Choose what we email you about. Account and billing mail is always
        sent.
      </p>

      <div className="emailpref-list">
        {EMAIL_AREAS.map((area) => {
          // Denylist polarity lives here and only here: a toggle is ON when
          // the area is NOT in the suppressed list.
          const subscribed = suppressed.indexOf(area.id) === -1;

          return (
            <label className="emailpref-row" key={area.id}>
              <span className="emailpref-text">
                <span className="emailpref-label">{area.label}</span>
                <span className="emailpref-hint">{area.hint}</span>
              </span>
              <input
                type="checkbox"
                role="switch"
                className="emailpref-switch-input"
                checked={subscribed}
                disabled={saving}
                onChange={() => toggleArea(area.id)}
              />
              <span className="emailpref-switch" aria-hidden="true">
                <span className="emailpref-switch-knob" />
              </span>
            </label>
          );
        })}
      </div>

      {error && (
        <div className="signin-error" style={{ marginTop: 14 }}>
          {error}
        </div>
      )}

      <div className="emailpref-actions">
        <button
          className="signin-btn emailpref-save-btn"
          onClick={handleSave}
          disabled={!dirty || saving}
        >
          {saving ? "Saving…" : "Save preferences"}
        </button>
        {saved && !dirty && <span className="emailpref-saved">Preferences saved.</span>}
      </div>
    </div>
  );
}
